import { Dispatch, FunctionComponent, useEffect, useState } from 'react';
import { VoucherCheckoutState } from '../vouchersState';
import { Card } from '../../../../components/Checkout/PaymentForm/Card';
import { CademyError } from '@shared/domain-shared';
import useCademyError from '../../../../hooks/useCademyError';
import ErrorAlert from '../../../../components/ErrorAlert';

type StripeSettings = {
    clientSecret: string;
    accountId: string;
};

const startPaymentForVoucher = async (voucherId: string): Promise<StripeSettings> => {
    const response = await fetch(
        `${process.env.NEXT_PUBLIC_CADEMY_API_URL}/apps/stripe/start-payment`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                voucher_id: voucherId,
            }),
        }
    );

    if (!response.ok) {
        if (response.headers.get('content-type') === 'application/problem+json') {
            const problemDetails = await response.json();
            const error = CademyError.fromObject(problemDetails);
            throw error;
        }
        if (response.headers.get('content-type') === 'application/json') {
            const errorDetails = await response.json();
            const error = CademyError.fromUnknown(errorDetails);
            throw error;
        }
        const error = await response.text();
        throw new Error(error);
    }

    const { client_secret, stripe_account_id } = await response.json();
    return {
        clientSecret: client_secret,
        accountId: stripe_account_id,
    };
};

export const Payment: FunctionComponent<{
    state: VoucherCheckoutState;
    dispatch: Dispatch<Partial<VoucherCheckoutState>>;
}> = ({ state, dispatch }) => {
    const [stripeSettings, setStripeSettings] = useState<StripeSettings | null>(null);
    const { inError, title, detail, setError } = useCademyError();

    useEffect(() => {
        if (!state.voucherId) {
            setError(
                new CademyError('vouchers/no-voucher-id', 'Missing required values', undefined, 500)
            );
            return;
        }
        startPaymentForVoucher(state.voucherId)
            .then((receivedStripeSettings) => {
                setStripeSettings(receivedStripeSettings);
            })
            .catch((error) => {
                setError(error);
            });
    }, [setError, state.voucherId]);

    if (!stripeSettings) {
        return (
            <div>
                <h3>Payment</h3>
                <ErrorAlert inError={inError} title={title} detail={detail} />
                <p>Loading...</p>
            </div>
        );
    }

    return (
        <div>
            <h3>Payment</h3>
            <ErrorAlert inError={inError} title={title} detail={detail} />
            <Card
                stripeClientSecret={stripeSettings.clientSecret}
                stripeAccountId={stripeSettings.accountId}
                onPaymentSuccess={() => dispatch({ currentStep: 'confirmation' })}
                billingDetails={{
                    name: `${state.buyer.firstName} ${state.buyer.lastName}`,
                    email: state.buyer.email || '',
                }}
            />
        </div>
    );
};
